import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X,
  Plus,
  Minus,
  Loader2,
  AlertCircle,
  CheckCircle,
  CreditCard,
  UserCheck,
  Users,
} from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const formatAmount = (cents) => {
  if (!cents && cents !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

// Individual seat type section component
const SeatTypeSection = ({
  label,
  icon: Icon,
  color,
  quantity,
  setQuantity,
  calculation,
  calculating,
  error,
  currentSeats,
}) => {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: 'text-blue-500',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      icon: 'text-purple-500',
    },
  };
  const colors = colorClasses[color] || colorClasses.blue;

  const incrementQuantity = () => setQuantity((q) => Math.min(q + 1, 100));
  const decrementQuantity = () => setQuantity((q) => Math.max(q - 1, 0));

  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${colors.icon}`} />
        <h3 className={`font-semibold ${colors.text}`}>{label}</h3>
      </div>

      {/* Current Status */}
      <div className="text-sm text-gray-600 mb-3">
        Currently: {currentSeats.used} of {currentSeats.total_available} used
      </div>

      {/* Quantity Selector */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-sm text-gray-600">Add:</span>
        <div className="flex items-center gap-2">
          <button
            onClick={decrementQuantity}
            disabled={quantity <= 0}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Minus className="w-4 h-4 text-gray-600" />
          </button>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
            className="w-16 text-center text-lg font-bold border border-gray-300 rounded-lg py-1"
            min="0"
            max="100"
          />
          <button
            onClick={incrementQuantity}
            disabled={quantity >= 100}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Price Info */}
      {quantity > 0 && (
        <div className="text-sm">
          {calculating ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Calculating...</span>
            </div>
          ) : error ? (
            <div className="text-red-600">{error}</div>
          ) : calculation ? (
            <div className="space-y-1">
              <div className="flex justify-between text-gray-600">
                <span>{formatAmount(calculation.unit_price)}/seat × {quantity}</span>
                <span className="font-medium">{formatAmount(calculation.total_monthly)}/mo</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Prorated today:</span>
                <span className="font-medium">{formatAmount(calculation.prorated_now)}</span>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const AddSeatsModal = ({
  organizationId,
  executiveSeats,
  eaSeats,
  onClose,
  onSuccess,
}) => {
  // Determine which seat types can be added (not unlimited)
  const canAddExecutives = executiveSeats && executiveSeats.total_available !== null && executiveSeats.can_add_more;
  const canAddEAs = eaSeats && eaSeats.total_available !== null && eaSeats.can_add_more;

  const [execQuantity, setExecQuantity] = useState(0);
  const [eaQuantity, setEaQuantity] = useState(0);
  const [execCalculation, setExecCalculation] = useState(null);
  const [eaCalculation, setEaCalculation] = useState(null);
  const [execCalculating, setExecCalculating] = useState(false);
  const [eaCalculating, setEaCalculating] = useState(false);
  const [execError, setExecError] = useState('');
  const [eaError, setEaError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  // Calculate executive pricing
  const calculateExecPrice = useCallback(async () => {
    if (!canAddExecutives || execQuantity < 1) {
      setExecCalculation(null);
      return;
    }

    setExecCalculating(true);
    try {
      const response = await axios.post(
        `${getApiBase()}/organizations/${organizationId}/seats/calculate`,
        { seat_type: 'executive', quantity: execQuantity },
        { headers: getAuthHeaders() }
      );
      setExecCalculation(response.data);
      setExecError('');
    } catch (err) {
      setExecError(err.response?.data?.detail || 'Failed to calculate pricing');
      setExecCalculation(null);
    } finally {
      setExecCalculating(false);
    }
  }, [organizationId, execQuantity, canAddExecutives]);

  // Calculate EA pricing
  const calculateEaPrice = useCallback(async () => {
    if (!canAddEAs || eaQuantity < 1) {
      setEaCalculation(null);
      return;
    }

    setEaCalculating(true);
    try {
      const response = await axios.post(
        `${getApiBase()}/organizations/${organizationId}/seats/calculate`,
        { seat_type: 'ea', quantity: eaQuantity },
        { headers: getAuthHeaders() }
      );
      setEaCalculation(response.data);
      setEaError('');
    } catch (err) {
      setEaError(err.response?.data?.detail || 'Failed to calculate pricing');
      setEaCalculation(null);
    } finally {
      setEaCalculating(false);
    }
  }, [organizationId, eaQuantity, canAddEAs]);

  // Debounced calculation effects
  useEffect(() => {
    const debounce = setTimeout(calculateExecPrice, 300);
    return () => clearTimeout(debounce);
  }, [calculateExecPrice]);

  useEffect(() => {
    const debounce = setTimeout(calculateEaPrice, 300);
    return () => clearTimeout(debounce);
  }, [calculateEaPrice]);

  // Calculate totals
  const totalProrated = (execCalculation?.prorated_now || 0) + (eaCalculation?.prorated_now || 0);
  const totalMonthlyIncrease = (execCalculation?.total_monthly || 0) + (eaCalculation?.total_monthly || 0);
  const hasSeatsToAdd = execQuantity > 0 || eaQuantity > 0;
  const isCalculating = execCalculating || eaCalculating;
  const hasErrors = (execQuantity > 0 && execError) || (eaQuantity > 0 && eaError);

  const handleSubmit = async () => {
    setLoading(true);
    setSubmitError('');

    try {
      // Use the bulk endpoint to create a single invoice and charge
      await axios.post(
        `${getApiBase()}/organizations/${organizationId}/seats/bulk`,
        {
          executive_quantity: execQuantity,
          ea_quantity: eaQuantity,
          confirm_charge: true,
        },
        { headers: getAuthHeaders() }
      );

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Failed to add seats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Add Seats</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Seats Added Successfully!
              </h3>
              <p className="text-gray-600">
                {execQuantity > 0 && `${execQuantity} Executive seat${execQuantity !== 1 ? 's' : ''}`}
                {execQuantity > 0 && eaQuantity > 0 && ' and '}
                {eaQuantity > 0 && `${eaQuantity} EA seat${eaQuantity !== 1 ? 's' : ''}`}
                {' '}have been added.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Select how many seats to add for each category. You can add seats for one or both categories.
              </p>

              {/* Seat Type Sections */}
              <div className="space-y-4 mb-6">
                {canAddExecutives && (
                  <SeatTypeSection
                    label="Executive Seats"
                    icon={UserCheck}
                    color="blue"
                    quantity={execQuantity}
                    setQuantity={setExecQuantity}
                    calculation={execCalculation}
                    calculating={execCalculating}
                    error={execError}
                    currentSeats={executiveSeats}
                  />
                )}

                {canAddEAs && (
                  <SeatTypeSection
                    label="EA Seats"
                    icon={Users}
                    color="purple"
                    quantity={eaQuantity}
                    setQuantity={setEaQuantity}
                    calculation={eaCalculation}
                    calculating={eaCalculating}
                    error={eaError}
                    currentSeats={eaSeats}
                  />
                )}
              </div>

              {/* Combined Totals */}
              {hasSeatsToAdd && !isCalculating && !hasErrors && (
                <div className="border border-gray-200 rounded-lg p-4 mb-4">
                  <h3 className="font-medium text-gray-900 mb-3">Order Summary</h3>
                  <div className="space-y-2 text-sm">
                    {execQuantity > 0 && execCalculation && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{execQuantity} Executive seat{execQuantity !== 1 ? 's' : ''}</span>
                        <span className="font-medium">{formatAmount(execCalculation.total_monthly)}/mo</span>
                      </div>
                    )}
                    {eaQuantity > 0 && eaCalculation && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{eaQuantity} EA seat{eaQuantity !== 1 ? 's' : ''}</span>
                        <span className="font-medium">{formatAmount(eaCalculation.total_monthly)}/mo</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Additional monthly:</span>
                        <span className="font-semibold">{formatAmount(totalMonthlyIncrease)}</span>
                      </div>
                    </div>
                    <div className="bg-blue-50 -mx-4 px-4 py-3 mt-3 rounded-b-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium text-blue-900">Charge today:</span>
                          <div className="text-xs text-blue-700">
                            (Prorated for remaining billing period)
                          </div>
                        </div>
                        <span className="text-xl font-bold text-blue-900">
                          {formatAmount(totalProrated)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 mb-4">
                <CreditCard className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Seats take effect immediately. You'll be charged a prorated amount for the current billing period.
                </span>
              </div>

              {/* Error */}
              {submitError && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-3 mb-4">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{submitError}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg sticky bottom-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || isCalculating || !hasSeatsToAdd || hasErrors}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {hasSeatsToAdd && !isCalculating && !hasErrors
                ? `Add Seats - ${formatAmount(totalProrated)}`
                : 'Add Seats'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddSeatsModal;
